import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { Close, OpenInNew, PlayArrow } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { shortsAPI, ShortsTarget } from '../../services/api';

interface Props {
  clipId: string | null;
  /** Targets for this clip, so a published one can link out to the real post. */
  targets?: ShortsTarget[];
  onClose: () => void;
}

const fmtDuration = (s?: number | null) => {
  if (!s && s !== 0) return null;
  const m = Math.floor(s / 60);
  return `${m}:${Math.round(s - m * 60).toString().padStart(2, '0')}`;
};

/**
 * Watch a short before it goes out.
 *
 * The point is catching a bad cut while it is still fixable: once a clip
 * publishes, Instagram and TikTok captions cannot be edited through any API we
 * have, and YouTube cannot replace a video's bytes at all.
 *
 * Sources are per FILE, not per platform. Facebook can carry its own 90s cut,
 * so a single player pointed at the main file would show you a video that is
 * not what Facebook will post — exactly the thing a preview exists to prevent.
 */
const ShortsPreview: React.FC<Props> = ({ clipId, targets, onClose }) => {
  const [active, setActive] = useState(0);

  const q = useQuery({
    queryKey: ['shorts', 'media', clipId],
    queryFn: () => shortsAPI.getClipMedia(clipId!),
    enabled: !!clipId,
    // Presigned URLs expire; don't hand back a stale one from cache.
    staleTime: 60_000,
    gcTime: 60_000,
  });

  useEffect(() => { setActive(0); }, [clipId]);

  const sources = q.data?.sources ?? [];
  const current = sources[active];
  const live = (targets ?? []).filter(
    (t) => t.status === 'published' && t.platform_url);

  return (
    <Dialog open={!!clipId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PlayArrow fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {q.data?.title || 'Preview'}
            </Typography>
            {current && (
              <Typography variant="caption" color="text.secondary">
                {current.filename}
                {current.duration_seconds
                  ? ` · ${fmtDuration(current.duration_seconds)}` : ''}
                {current.width && current.height
                  ? ` · ${current.width}×${current.height}` : ''}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        )}
        {q.isError && (
          <Alert severity="error">Could not load this clip's media.</Alert>
        )}
        {q.data && sources.length === 0 && (
          <Alert severity="warning">
            Nothing staged to play — this clip has no file in S3.
          </Alert>
        )}

        {sources.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            {sources.map((s, i) => (
              <Tooltip key={s.url} title={
                s.platforms.length
                  ? `Goes to ${s.platforms.join(', ')}`
                  : 'Not assigned to a platform'}>
                <Chip
                  label={s.label}
                  size="small"
                  color={i === active ? 'primary' : 'default'}
                  variant={i === active ? 'filled' : 'outlined'}
                  onClick={() => setActive(i)}
                />
              </Tooltip>
            ))}
          </Stack>
        )}

        {current && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="video"
              key={current.url}
              src={current.url}
              poster={q.data?.thumbnail_url || undefined}
              controls
              playsInline
              preload="metadata"
              sx={{
                maxHeight: '62vh', maxWidth: '100%', borderRadius: 1,
                bgcolor: 'common.black',
              }}
            />
          </Box>
        )}

        {sources.length > 1 && current && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1.5 }}>
            This clip has more than one render. You're watching the{' '}
            <strong>{current.label.toLowerCase()}</strong>
            {current.platforms.length
              ? ` — what ${current.platforms.join(' and ')} will post.`
              : '.'}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        {live.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Already live:
            </Typography>
            {live.map((t) => (
              <Button
                key={t.target_id}
                size="small"
                endIcon={<OpenInNew fontSize="small" />}
                href={t.platform_url!}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.platform}
              </Button>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShortsPreview;
